import { ButtonSpeedDial } from "@/components";
import useSyncMutation from "@/hooks/useSyncMutation";
import { getMessageUnreadOptions } from "@/queries";
import { InboxAlertsSearchParamSchema } from "@/schemas";
import { useSyncStore } from "@/stores/sync";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
	Link,
	Outlet,
	createFileRoute,
	retainSearchParams,
} from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import FilterAlerts from "../-components/FilterAlerts";

export const Route = createFileRoute("/dashboard/inbox/alerts")({
	validateSearch: zodSearchValidator(InboxAlertsSearchParamSchema),
	search: {
		middlewares: [retainSearchParams(["tab"])],
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation("dashboard");
	const inboxSyncLastUnix = useSyncStore((s) => s.inboxSyncLastUnix);
	const { inboxMessagesSync } = useSyncMutation();
	const onClickSync = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			return inboxMessagesSync.mutate({
				last_requested_at: inboxSyncLastUnix,
			});
		},
		[inboxSyncLastUnix, inboxMessagesSync],
	);

	const unreadQuery = useSuspenseQuery(getMessageUnreadOptions());

	const unreads = useMemo(
		() => ({
			inbox: unreadQuery.data.inbox,
		}),
		[unreadQuery.data],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (!inboxMessagesSync.isPending) {
			inboxMessagesSync.mutate({
				last_requested_at: 0,
			});
		}
	}, []);

	return (
		<>
			<FilterAlerts />
			<Outlet />
			<div className="bg-gradient-dashboard3 container py-1">
				<div className="flex flex-row justify-between items-center gap-2 py-1">
					<div className="flex-1 flex  justify-center items-center">
						<Link to="/dashboard">
							<svg
								width="25"
								height="24"
								viewBox="0 0 25 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<title>{t("dashboard:home.logo")}</title>
								<g clipPath="url(#clip0_6250_2757)">
									<path
										d="M9.9469 4.54861C8.6027 4.59559 7.35246 4.67326 6.22937 4.75907C6.30955 3.94416 6.40413 3.12111 6.5175 2.29492C7.60113 1.62407 8.80439 1.13362 10.0878 0.85614C10.0409 1.87337 9.99074 3.11798 9.94627 4.54861H9.9469Z"
										fill="white"
									/>
									<path
										d="M10.7261 4.52361C10.7706 3.03535 10.8232 1.74877 10.8702 0.717764C11.4026 0.640093 11.9456 0.601257 12.5 0.601257C13.0543 0.601257 13.6199 0.6426 14.163 0.72027C14.21 1.74878 14.2601 3.02407 14.3071 4.50419C13.065 4.48477 11.8648 4.49292 10.7255 4.52361H10.7261Z"
										fill="white"
									/>
									<path
										d="M18.5181 2.31749C18.629 3.11862 18.7205 3.91975 18.7981 4.71274C17.5366 4.61315 16.2951 4.55176 15.0862 4.51857C15.0418 3.10484 14.9923 1.87402 14.9478 0.864929C16.2313 1.14492 17.437 1.64414 18.5181 2.31749Z"
										fill="white"
									/>
									<path
										d="M9.91932 19.2014C9.95565 20.4905 10.0051 21.8071 10.069 23.1375C8.77995 22.8575 7.57418 22.3614 6.48742 21.6875C6.39848 20.9308 6.30452 20.0213 6.21558 18.9903C7.33553 19.0792 8.58014 19.1538 9.9187 19.2008L9.91932 19.2014Z"
										fill="white"
									/>
									<path
										d="M14.3353 19.249C14.2989 20.5687 14.2495 21.9129 14.1831 23.2766C13.6344 23.3567 13.0713 23.3987 12.5006 23.3987C11.93 23.3987 11.3888 23.3574 10.8514 23.2797C10.785 21.9073 10.7349 20.555 10.696 19.2296C11.4389 19.249 12.2094 19.2602 12.9998 19.2602C13.4408 19.2602 13.8843 19.2577 14.3359 19.249H14.3353Z"
										fill="white"
									/>
									<path
										d="M18.812 19.041C18.7262 20.042 18.6347 20.9233 18.5489 21.6636C17.4621 22.3458 16.2564 22.8475 14.9648 23.1331C15.0287 21.8133 15.0757 20.5105 15.1145 19.2327C16.3146 19.202 17.5536 19.1412 18.8126 19.0416L18.812 19.041Z"
										fill="white"
									/>
									<path
										d="M5.17644 14.4642C5.22092 15.7889 5.28981 17.0254 5.37312 18.1454C4.35025 18.0539 3.45767 17.9568 2.72857 17.871C2.07715 16.7899 1.60048 15.5923 1.3399 14.317C2.63712 14.3777 3.91806 14.4278 5.17644 14.4642Z"
										fill="white"
									/>
									<path
										d="M9.83603 14.5555C9.8473 15.8083 9.86672 17.103 9.89992 18.4278C8.53881 18.3777 7.28042 18.3 6.15483 18.2117C6.07152 17.0836 5.99698 15.8302 5.95502 14.486C7.2798 14.5224 8.57764 14.5443 9.83603 14.5555Z"
										fill="white"
									/>
									<path
										d="M12.2475 14.5669C12.9904 14.5669 13.717 14.5644 14.421 14.5556C14.4098 15.8253 14.3903 17.1338 14.3572 18.4755C13.0762 18.4974 11.8454 18.4867 10.676 18.4535C10.6428 17.1231 10.6233 15.8203 10.6121 14.5619C11.1664 14.5644 11.7126 14.5675 12.2475 14.5675V14.5669Z"
										fill="white"
									/>
									<path
										d="M15.1971 14.5499C16.5858 14.533 17.8861 14.5086 19.0781 14.4779C19.0337 15.8447 18.9616 17.1168 18.8733 18.2587C17.6011 18.3614 16.3509 18.4253 15.1339 18.4585C15.167 17.1193 15.1865 15.814 15.1977 14.5499H15.1971Z"
										fill="white"
									/>
									<path
										d="M19.8567 14.4535C21.3262 14.4121 22.6096 14.362 23.6626 14.3151C23.3964 15.6041 22.9141 16.8124 22.2545 17.9023C21.3838 18.0188 20.5163 18.1159 19.6569 18.1936C19.7427 17.0598 19.8123 15.8014 19.8567 14.4541V14.4535Z"
										fill="white"
									/>
									<path
										d="M5.15389 13.6881C3.85918 13.6492 2.5369 13.5966 1.20398 13.5327C1.13758 13.031 1.10126 12.518 1.10126 12C1.10126 11.3955 1.14823 10.8024 1.23968 10.223C2.56196 10.1566 3.86732 10.1065 5.14826 10.0676C5.1232 11.3204 5.12884 12.5349 5.15389 13.6881Z"
										fill="white"
									/>
									<path
										d="M2.89201 5.86524C3.59042 5.77943 4.42475 5.69049 5.37559 5.60718C5.27035 6.85742 5.20646 8.09075 5.16763 9.28837C3.92866 9.3247 2.66715 9.37419 1.38934 9.43557C1.68624 8.14963 2.20175 6.94323 2.89201 5.86524Z"
										fill="white"
									/>
									<path
										d="M9.84167 9.97615C9.82789 11.1851 9.82226 12.4572 9.83353 13.7795C8.56951 13.7657 7.26665 13.7432 5.9306 13.71C5.90554 12.5455 5.89991 11.3178 5.92496 10.0451C7.26415 10.0119 8.57515 9.98993 9.84167 9.97552V9.97615Z"
										fill="white"
									/>
									<path
										d="M6.16043 5.54142C7.29166 5.45247 8.55819 5.3723 9.92494 5.32532C9.89425 6.51167 9.86669 7.80889 9.84727 9.19756C8.58325 9.20883 7.28039 9.23076 5.94684 9.26709C5.98317 8.05317 6.05207 6.80544 6.16043 5.54142Z"
										fill="white"
									/>
									<path
										d="M14.3296 5.27775C14.3628 6.47538 14.3904 7.78951 14.4098 9.1976C13.204 9.18632 11.9369 9.18382 10.6234 9.19196C10.6428 7.79515 10.6703 6.48916 10.7035 5.3003C11.8542 5.2671 13.0706 5.25896 14.3296 5.27838V5.27775Z"
										fill="white"
									/>
									<path
										d="M19.0863 9.27522C17.8887 9.24453 16.5802 9.21947 15.1859 9.20319C15.1665 7.80074 15.1389 6.48911 15.1057 5.29462C16.3284 5.32781 17.5893 5.38857 18.8702 5.49443C18.9786 6.77787 19.0475 8.0444 19.0863 9.27522Z"
										fill="white"
									/>
									<path
										d="M19.1082 10.057C19.1333 11.321 19.1277 12.5405 19.1026 13.6993C17.905 13.7325 16.5965 13.7576 15.1997 13.7739C15.2109 12.4541 15.2053 11.1876 15.194 9.98181C16.5965 9.99872 17.9081 10.0232 19.1082 10.0564V10.057Z"
										fill="white"
									/>
									<path
										d="M23.61 9.43561C22.5677 9.38864 21.3062 9.34166 19.8649 9.29719C19.826 8.08014 19.7596 6.8299 19.6544 5.56024C20.4612 5.63227 21.2705 5.72373 22.0854 5.82896C22.7869 6.91822 23.3106 8.13276 23.61 9.43561Z"
										fill="white"
									/>
									<path
										d="M23.8987 12C23.8987 12.5186 23.8624 13.031 23.796 13.5327C22.7261 13.5797 21.4039 13.6323 19.8793 13.6768C19.9043 12.5293 19.91 11.3235 19.8849 10.0757C21.3901 10.1202 22.6986 10.1728 23.7603 10.2198C23.8517 10.7992 23.8987 11.3949 23.8987 11.9993V12Z"
										fill="white"
									/>
								</g>
								<defs>
									<clipPath id="clip0_6250_2757">
										<rect
											width="22.8"
											height="22.8"
											fill="white"
											transform="translate(1.09998 0.599976)"
										/>
									</clipPath>
								</defs>
							</svg>
						</Link>
					</div>
					<Link to="/dashboard/camera">
						<IconButton
							size="medium"
							className="!bg-gradient-camera2 !w-14 !h-14 !text-white !shadow-xl"
							title={t("dashboard:home.camera")}
						>
							<CameraAltIcon />
						</IconButton>
					</Link>
					<div className="flex-1 flex  justify-center items-center">
						<Link to="/dashboard/inbox">
							<Badge badgeContent={unreads.inbox} color="error">
								<InboxRoundedIcon className="text-white" />
							</Badge>
						</Link>
					</div>
				</div>
			</div>
			<ButtonSpeedDial
				bottom={0}
				syncProcessing={inboxMessagesSync.isPending}
				onClickSync={onClickSync}
			/>
		</>
	);
}
